import { blueGrey, grey } from '@mui/material/colors'
import { alpha, createTheme } from '@mui/material/styles'
import {
  accordionBaseStyles,
  accordionDetailsStyles,
  accordionSummaryBaseStyles,
  autocompleteBaseStyles,
  breakpointValues,
  cardBaseStyles,
  collapseStyles,
  dataGridCheckboxStyles,
  dataGridVirtualScrollerStyles,
  dialogPaperStyles,
  formControlStyles,
  formLabelStyles,
  gridBaseStyles,
  hiddenSearchColumnStyles,
  inputBaseInputStyles,
  inputLabelOutlinedStyles,
  menuStyles,
  paperBaseStyles,
  quillDarkStyles,
  quillLightStyles,
  selectBaseStyles,
  tableContainerStyles,
  tableStyles,
  themeScrollbarStyles,
  tooltipBaseStyles,
} from '../tokens/components'

const paper = 'rgb(22, 28, 42)'
const section = 'rgb(30, 39, 53)'
const def = 'rgb(49, 57, 70)'

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      lighter: blueGrey[100],
      main: blueGrey[700],
      contrastText: grey[50],
    },
    secondary: {
      main: grey[900],
      contrastText: grey[200],
    },
    background: {
      image: 'radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)',
      divider: 'rgb(72, 95, 129)',
      pinnedrows: 'rgba(139, 165, 196, 0.28)',
      default: def,
      defaultdim: 'rgba(26, 22, 22, 0.5)',
      section: 'rgba(26, 22, 22, 0.5)',
      subtasksection: 'rgba(30, 39, 53, 0.8)',
      mentions: section,
      header: 'rgb(26, 22, 22)',
      main: section,
      tooltip: 'rgba(0, 0, 0, 0.87)',
      input: 'rgb(38, 47, 67)',
      icon: 'rgb(44, 51, 61)',
      sectionContent: 'rgb(60, 72, 85)',
      selected: 'rgb(26, 22, 22)',
      timelineGrid: 'rgba(43, 67, 123, 0.5)',
      help: 'rgb(43, 67, 123)',
      paper,
      disabled: 'rgba(22, 28, 42, 0.3)',
      toolbar: 'rgb(40, 46, 59)',
      loading: 'rgb(107, 185, 240)',
      boxShadow: '0 1px 3px rgba(255, 255, 255, 0.1)',
      isDragging: '0 4px 6px rgba(255, 255, 255, 0.1)',
      overlay: 'rgba(26, 22, 22, 0.1)',
      thumbnail: 'rgba(202, 65, 92, 0.8)',
      details: 'rgb(41, 54, 69)',
      fielddisabled: 'rgb(47, 53, 65)',
      // scrollbar_thumb: 'rgb(29, 26, 26)',
      scrollbar_thumb: 'rgba(167,51,51,1)',
      scrollbar_hover: 'rgb(51, 50, 50)',
      dialogtext: '#2E3B4E',
      snackbar: 'transparent',
      postdated: 'rgba(94, 114, 226, 0.56)',
      gradientDefault: `linear-gradient(180deg, ${paper} 0%, ${section} 38%, ${def} 100%)`,
      gradientSection: `linear-gradient(180deg, ${section} 0%, ${def} 100%)`,
      gradientPaper: `linear-gradient(180deg, ${paper} 0%, ${section} 100%)`,
      vignette: `radial-gradient(1200px 800px at 50% 0%, ${alpha('#000', 0.0)} 40%, ${alpha('#000', 0.25)} 100%)`,
      button: 'rgb(33, 100, 160)',
    },
    text: {
      primary: 'rgba(255,255,255,1)',
      contrast: 'rgba(0,0,0,1)',
      shadow: 'rgba(0,0,0,0.5)',
      // highlight: 'rgb(107, 185, 240)',
      highlight: '#6BB9F0',
      ai_agent_text: '#90A4AE',
      subtask: 'rgb(50, 158, 235)',
      secondary: 'rgba(255,255,255,0.5)',
      disabled: 'rgba(128, 124, 124, 0.8)',
      tertiary: 'rgba(255,255,255,0.3)',
      resetFilter: 'rgb(255, 165, 93)',
      chip: 'rgba(0,0,0,1)',
      admin: 'rgba(255, 123, 187, 1)',
      success: 'rgb(80, 200, 120)',
      successdim: 'rgba(129, 230, 163, 1)',
      warning: 'rgb(249, 167, 49)',
      dirty: 'rgba(72, 113, 247, 0.3)',
      error: 'rgba(220, 20, 60, 1)',
      errordim: 'rgba(226, 70, 70, 0.5)',
      link: 'rgb(173, 216, 230)',
      revise: 'rgb(255, 131, 88)',
      review: 'rgb(33, 201, 223)',
      htmlText: 'rgba(255,255,255,1)',
      parent: 'rgba(67, 85, 241, 1)',
      size: 12,
    },
    badge: {
      dot: 'rgb(255, 165, 93)',
    },
    chip: {
      sizeSmall: { height: 15, width: 15 },
    },
    card: {
      default: 'rgb(18, 26, 41)',
    },
    icon: {
      complete: 'rgb(5, 190, 113)',
      warning: 'rgb(249, 167, 49)',
      decline: 'rgba(220, 20, 60, 0.8)',
      deals: '#fbc11e',
      teams: 'rgb(99, 102, 241)',
      organizations: 'rgb(114, 229, 218)',
      approvals: 'rgba(205, 254, 194, 1)',
      contacts: 'rgb(255, 123, 187)',
      nco: 'rgb(52, 211, 145)',
      accounting: 'rgb(107, 185, 240)',
      teamtasks: 'rgb(247, 63, 236)',
      website: 'rgb(84, 126, 240)',
      creative: 'rgb(150, 88, 238)',
      seo: 'rgb(238, 111, 141)',
      mtb: 'rgb(20, 119, 248)',
      products: 'rgb(224, 110, 215)',
      campaignChecklist: 'rgb(255, 131, 88)',
      projects: 'rgb(245, 108, 53)',
      insight: 'rgb(65, 190, 169)',
      observation: 'rgb(168, 202, 255)',
      leasingPercentage: 'rgba(230, 181, 21, 1)',
      pandadoc: 'rgb(36, 133, 103)',
      dashboard: 'rgb(5, 190, 113)',
    },
    list: {
      selected: 'rgba(130, 178, 240, 1)',
      focused: 'rgba(66, 85, 110, 1)',
    },
    tertiary: {
      lighter: grey[50],
      light: grey[100],
      main: grey[400],
      dark: grey[700],
      darker: grey[900],
    },
    myStyledComponentBackground: 'black',
    action: {
      selected: alpha('#90caf9', 0.16),
      selectedOpacity: 0.16,
      hoverOpacity: 0.08,
    },
  },
  typography: {
    fontFamily: '"Funnel Sans", sans-serif',
    fontSize: 14,
    h1: {
      fontSize: '2.5rem',
    },
  },
  spacing: 8,
  hover: '#424242',
  breakpoints: {
    values: breakpointValues,
  },
  shadows: Array(25)
    .fill('none')
    .map((_, index) =>
      index === 0
        ? 'none'
        : `0px ${index}px ${index * 2}px rgba(0, 0, 0, 0.14),
    0px ${index / 2}px ${index}px rgba(0, 0, 0, 0.12),
    0px ${index}px ${index * 2}px rgba(0, 0, 0, 0.2)`
    ) as any,

  components: {
    MyCustomQuill: {
      light: quillLightStyles,
      dark: quillDarkStyles,
    },
    MuiCssBaseline: {
      styleOverrides: theme => ({
        body: {
          backgroundImage: [
            theme.palette.background.image,
            theme.palette.background.vignette,
            theme.palette.background.gradientDefault,
          ].join(','),
          backgroundColor: theme.palette.background.default,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        },
        '@media (prefers-reduced-motion: reduce)': {
          body: {
            backgroundAttachment: 'scroll, scroll, scroll',
          },
        },
        root: {
          ...themeScrollbarStyles(theme),
        },
      }),
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...paperBaseStyles(theme),
          backgroundImage: theme.palette.background.gradientPaper,
        }),
      },
    },
    MuiCollapse: {
      styleOverrides: collapseStyles,
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: 'rgba(255,255,255,1)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
          '&.Mui-selected': {
            color: 'rgb(92, 179, 253)',
            backgroundColor: `${alpha(theme.palette.background.defaultdim, 0.45)} !important`,
            fontWeight: 'bold',
          },
          '&.MuiTab-textColorPrimary.Mui-disabled': {
            color: 'rgba(255,255,255,0.4) !important',
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.01)',
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          color: 'rgba(255,255,255,1)',
        },
      },
    },
    MuiTable: {
      styleOverrides: tableStyles,
    },
    MuiTableContainer: {
      styleOverrides: tableContainerStyles,
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgb(26 22 22)',
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {},
      },
    },
    MuiMenu: {
      styleOverrides: menuStyles,
    },
    MuiMenuList: {
      styleOverrides: {
        root: {
          backgroundColor: '#17191E',
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...themeScrollbarStyles(theme),
        }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...themeScrollbarStyles(theme),
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: 'rgb(255,255,255,0.5)',
          '&.Mui-focused': {
            color: 'white',
          },
        },
        outlined: inputLabelOutlinedStyles,
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: ({ theme }) => ({
          height: 'auto',
          backgroundColor: '#17191E',
          '&.Mui-focused .MuiListItemButton-root, &.Mui-focusVisible .MuiListItemButton-root': {
            backgroundColor: theme.palette.background.toolbar,
          },
          '&[aria-selected="true"] .MuiListItemButton-root': {
            backgroundColor: theme.palette.background.help,
          },
          '&:hover': {
            backgroundColor: 'rgb(30, 43, 59)',
            cursor: 'pointer',
          },
        }),
        input: {
          ...autocompleteBaseStyles.input,
          color: 'rgba(255,255,255,1)',
        },
        root: autocompleteBaseStyles.root,
        popper: autocompleteBaseStyles.popper,
        paper: {
          ...autocompleteBaseStyles.paper,
        },
        inputRoot: {
          '& .MuiOutlinedInput-root': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#6cd3e7',
              borderWidth: '1px',
              padding: '0px',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'blue',
              borderWidth: '2px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white',
              borderWidth: '2px',
            },
          },
        },
        listbox: ({ theme }) => themeScrollbarStyles(theme),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        body: {
          textAlign: 'center',
          margin: '5px',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          textOverflow: 'hidden',
          height: '2px',
          lineHeight: '1.25',
          fontSize: '14px',
          justifyContent: 'center',
          alignItems: 'center',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {},
        input: {
          ...inputBaseInputStyles,
          color: 'white',
        },
      },
    },
    MuiFormControl: {
      styleOverrides: formControlStyles,
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgb(89, 224, 158)',
            borderWidth: '1px',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          '& > *': {
            transition: 'transform 0.3s ease',
          },
          '&:hover > *': {
            transform: 'scale(1.07)',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          ...formLabelStyles.root,
          '&.Mui-focused': {
            color: 'rgb(89, 224, 158)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {},
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          ...selectBaseStyles,
          '&.Mui-disabled': {},
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            backgroundColor: `${alpha(theme.palette.background.help, 0.25)} !important`,
          },
          '&.Mui-selected:hover': {
            backgroundColor: `${alpha(theme.palette.background.section, 0.2)} !important`,
          },
        }),
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          ...accordionBaseStyles,
          backgroundColor: 'rgb(30 43 59)',
          color: blueGrey[900],
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        ...accordionSummaryBaseStyles,
        root: {
          ...accordionSummaryBaseStyles.root,
          backgroundColor: '#1E2735',
          color: 'white',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          ...accordionDetailsStyles.root,
          backgroundColor: 'rgb(19,26,41)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          ...tooltipBaseStyles,
          backgroundColor: 'rgba(0,0,0,0.9)',
          color: 'white',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: dialogPaperStyles,
      },
    },
    MuiGrid: {
      styleOverrides: {
        ...gridBaseStyles,
        root: ({ theme }) => ({
          ...gridBaseStyles.root,
          ...themeScrollbarStyles,
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          ...cardBaseStyles,
          backgroundColor: 'transparent',
          color: 'rgba(255,255,255,1)',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...cardBaseStyles,
          backgroundColor: 'transparent',
          color: 'rgba(255,255,255,1)',
          ...themeScrollbarStyles,
        }),
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          color: 'white',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          color: 'white',
          borderColor: 'white',
          '& .MuiChip-deleteIconSmall': {
            color: 'white',
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: 0,
          fontFamily: '"Funnel Sans", sans-serif',
          ...dataGridVirtualScrollerStyles,
          background: 'transparent',
          // Scrollbar styles
          '& .MuiDataGrid-scrollbar': {
            scrollbarWidth: 'auto',
            scrollbarColor: `${theme.palette.text.primary} ${theme.palette.background.default}`,
          },
          '&': {
            scrollbarColor: `${theme.palette.text.primary} ${theme.palette.background.default}`,
          },
          // '& .MuiDataGrid-scrollbarContent': {
          //   backgroundColor: '#4e4949',
          // },
          // '& ::-webkit-scrollbar-corner': {
          //   backgroundColor: theme.palette.background.details,
          // },
          // '& .MuiDataGrid-corner'
          '& .MuiDataGrid-cell': {
            color: 'rgba(255, 255, 255)',
            '& > *': {
              transition: 'transform 0.3s ease',
            },
            '&:hover > *': {
              transform: 'scale(1.07)',
            },
          },
          '& .MuiDataGrid-columnHeaders': {
            fontSize: '14px',
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: '#fafafa',
          },
          '& .MuiDataGrid-row': {
            fontSize: '12px',
            boxShadow: `0 1px 0 0 ${theme.palette.text.disabled}`,
            // backgroundColor: 'inherit',
            '&:hover': {
              backgroundColor: 'inherit',
            },
          },
          '.MuiDataGrid-row.Mui-selected': {
            '&:hover': {
              backgroundColor: 'rgba(87, 125, 173, 0.4)',
            },
            backgroundColor: 'rgba(87, 125, 173, 0.4)',
          },
          ...dataGridCheckboxStyles,
          '& .MuiDataGrid-pinnedRows--bottom': {
            p: 0,
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            backgroundColor: 'rgb(49, 57, 70)',
            width: '100%',
            minWidth: '100%',
            borderRadius: '0 !important',
            margin: '0 !important',
            borderTop: `2px solid ${theme.palette.divider} !important`,
          },
          '& .MuiDataGrid-filler--pinnedRight': {
            backgroundColor: `transparent !important`,
          },
          '& .MuiDataGrid-filler--pinnedLeft': {
            backgroundColor: `transparent !important`,
          },
          '.MuiDataGrid-footerCell': {
            color: 'rgba(255,255,255,1)',
          },
          '.MuiDataGrid-footerCell:hover': {
            cursor: 'default',
          },
          '& .MuiDataGrid-toolbarContainer': {
            backgroundColor: 'rgb(49, 57, 70)',
            overflow: 'visible',
            position: 'relative',
            zIndex: 10,
          },
          '& .MuiDataGrid-toolbarContainer .MuiToolbar-root': {
            overflow: 'visible',
            paddingTop: '12px',
            minHeight: '56px',
          },
          '& .MuiDataGrid-container--top [role=row]': {
            backgroundColor: 'rgb(49, 57, 70)',
            borderLeft: '4px solid transparent',
            borderRight: '4px solid transparent',
            borderTop: '4px solid rgb(30, 39, 53)',
          },
          // Aggregation row styling
          '& .MuiDataGrid-row[data-id="auto-generated-group-footer-root"]': {
            backgroundColor: `${theme.palette.background.defaultdim} !important`,
          },
          '& .MuiDataGrid-row[data-id="auto-generated-group-footer-root"] .MuiDataGrid-cell': {
            backgroundColor: 'inherit !important',
          },
          '& .MuiDataGrid-row--lastVisible[data-id="auto-generated-group-footer-root"]': {
            zIndex: 100,
            backgroundColor: theme.palette.background.defaultdim,
            width: '100%',
            pointerEvents: 'none',
          },

          // Pinned columns - custom background with visible borders
          '& .MuiDataGrid-pinnedColumns--left, & .MuiDataGrid-pinnedColumns--right': {
            backgroundColor: theme.palette.background.input,
          },
          '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
            backgroundColor: `${theme.palette.background.input} !important`,
            // Add visible borders using boxShadow to match grid styling
            boxShadow: `
                        1px 0 0 0 ${alpha(theme.palette.divider, 0.12)},
                        0 1px 0 0 ${alpha(theme.palette.divider, 0.12)}
                      `,
          },
          // Maintain pinned column borders on hover
          '& .MuiDataGrid-cell--pinnedLeft:hover, & .MuiDataGrid-cell--pinnedRight:hover': {
            boxShadow: `
                        1px 0 0 0 ${alpha(theme.palette.divider, 0.12)},
                        0 1px 0 0 ${alpha(theme.palette.divider, 0.12)}
                      `,
          },
          // Pinned column headers with borders
          '& .MuiDataGrid-pinnedColumnHeaders--left, & .MuiDataGrid-pinnedColumnHeaders--right': {
            backgroundColor: theme.palette.background.input,
          },
          '& .MuiDataGrid-pinnedColumnHeaders--left .MuiDataGrid-columnHeader, & .MuiDataGrid-pinnedColumnHeaders--right .MuiDataGrid-columnHeader': {
            backgroundColor: `${theme.palette.background.input} !important`,
            boxShadow: `
                        1px 0 0 0 ${alpha(theme.palette.divider, 0.12)},
                        0 1px 0 0 ${alpha(theme.palette.divider, 0.12)}
                      `,
          },
          // Fix pinned column background in aggregation row
          '& .MuiDataGrid-pinnedColumns .MuiDataGrid-row[data-id="auto-generated-group-footer-root"]': {
            backgroundColor: theme.palette.background.paper,
          },
          // Virtual scroller backgrounds
          '& .MuiDataGrid-virtualScrollerContent, & .MuiDataGrid-virtualScrollerRenderZone': {
            backgroundColor: 'transparent',
          },
          // Bottom container styling
          '& .MuiDataGrid-container--bottom': {
            padding: '0 !important',
            margin: '0 !important',
          },
          '& .MuiDataGrid-scrollArea--bottom': {
            padding: '0 !important',
            margin: '0 !important',
          },
          // Bottom pinned rows styling
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row, & .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-cell': {
            backgroundColor: `${theme.palette.background.paper} !important`,
          },
          // Server aggregation row styling
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"]': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            fontWeight: 600,
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"] .MuiDataGrid-cell': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            pointerEvents: 'none',
          },
          // Disable hover effects on server aggregation row
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"]:hover': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            cursor: 'default',
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"]:hover .MuiDataGrid-cell': {
            backgroundColor: `${theme.palette.background.paper} !important`,
          },
          // Disable cell hover effects on aggregation row
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-row[data-id="__SERVER_AGGREGATIONS_ROW__"] .MuiDataGrid-cell:hover': {
            backgroundColor: `${theme.palette.background.paper} !important`,
            cursor: 'default',
          },
          // ============================================================================
          // Row Classes - Special row styling
          // ============================================================================
          '& .high-priority-row': {
            backgroundColor: 'rgba(255, 0, 0, 0.1) !important',
          },
          '& .postdated': {
            backgroundColor: 'rgba(255, 193, 7, 0.15) !important',
          },
          '& .postdated:hover': {
            backgroundColor: 'rgba(255, 193, 7, 0.15) !important',
          },
          '& .postdated .MuiDataGrid-cell': {
            backgroundColor: 'inherit !important',
          },
          '& .postdated .MuiDataGrid-cell:hover': {
            backgroundColor: 'inherit !important',
          },
          '& .MuiDataGrid-row.highlighted-row': {
            opacity: '1 !important',
            backgroundColor: 'rgba(201, 52, 52, 0.2) !important',
            zIndex: '1500 !important',
          },
          '& .MuiDataGrid-row.Mui-selected.MuiDataGrid-row--firstVisible.MuiDataGrid-row--lastVisible.highlighted-row': {
            opacity: '1 !important',
            backgroundColor: 'rgba(228, 22, 22, 0.2) !important',
            zIndex: '1500 !important',
          },
          '& .task-row': {
            backgroundColor: 'rgba(30, 39, 53, 1)',
            fontWeight: 600,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          },
          '& .subtask-row': {
            backgroundColor: theme.palette.background.default,
            paddingLeft: '32px',
          },
          '& .disabled-selection': {
            opacity: '0.3 !important',
            pointerEvents: 'none',
            backgroundColor: 'rgba(100, 100, 100, 0.05) !important',
          },
          '& .product-row': {
            backgroundColor: 'rgba(100, 100, 100, 0.1)',
          },
          '& .MuiDataGrid-row.delete-row': {
            border: '1px solid',
            borderColor: 'rgb(238, 124, 124) !important',
            transition: 'background-color 0.3s ease',
            zIndex: '1900 !important',
            position: 'relative !important',
          },
          // ============================================================================
          // Org Name Header Styling
          // ============================================================================
          '& .org-name-header .MuiDataGrid-columnHeaderTitleContainer': {
            flex: 1,
            minWidth: 0,
          },
          '& .org-name-header .MuiDataGrid-columnHeaderTitleContainerContent': {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
          },
          '& .org-name-header .MuiDataGrid-iconButtonContainer': {
            flexShrink: 0,
          },
          '& .org-name-header .MuiStack-root': {
            display: 'flex',
            flex: '1 1 auto',
            minWidth: 0,
          },
          '& .org-name-header .MuiStack-root > .MuiTypography-root': {
            flex: '1 1 auto',
            minWidth: 0,
          },
          '& .org-name-header .MuiStack-root > .MuiBox-root': {
            marginLeft: 'auto',
            display: 'flex',
            gap: '8px',
            flex: '0 0 auto',
          },
          // ============================================================================
          // Pinned Rows Top Styling
          // ============================================================================
          '& .MuiDataGrid-pinnedRows, & .MuiDataGrid-pinnedRows--top': {
            borderRadius: '0 !important',
            padding: '0 !important',
            margin: '0 !important',
            border: '0 !important',
            width: '100%',
          },

          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-row, & .MuiDataGrid-pinnedRows--top .MuiDataGrid-cell': {
            borderRadius: '0 !important',
            backgroundColor: theme.palette.background.defaultdim,
          },
          '& .MuiDataGrid-container--top': {
            padding: '0 !important',
            margin: '0 !important',
            border: '0 !important',
            overflow: 'visible',
          },
          '& .MuiDataGrid-container--top [role="row"]': {
            border: '0 !important',
          },
          '& .MuiDataGrid-pinnedRows--top': {
            paddingBottom: '22px',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-cell': {
            paddingTop: '2px',
            paddingBottom: '2px',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCellToggle': {
            display: 'none !important',
            pointerEvents: 'none',
          },
          '& .MuiDataGrid-pinnedRows--top .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCell, & .MuiDataGrid-pinnedRows--top .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-cellContent': {
            marginLeft: '0 !important',
            paddingLeft: '0 !important',
          },
          // ============================================================================
          // Pinned Rows Bottom - Additional Styling
          // ============================================================================
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-virtualScrollerRenderZone, & .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-virtualScrollerContent': {
            backgroundColor: `${theme.palette.background.paper} !important`,
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCellToggle': {
            display: 'none !important',
            pointerEvents: 'none',
          },
          '& .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-groupingCriteriaCell, & .MuiDataGrid-pinnedRows--bottom .MuiDataGrid-treeDataGroupingCell .MuiDataGrid-cellContent': {
            marginLeft: '0 !important',
            paddingLeft: '0 !important',
          },
          // ============================================================================
          // Drawer Open State - Dim rows except current
          // ============================================================================
          '&.has-drawer-open .MuiDataGrid-row': {
            position: 'relative',
            opacity: 0.9,
          },
          '&.has-drawer-open .MuiDataGrid-row:not(.drawer-current-row)::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.35)',
            pointerEvents: 'none',
          },
          '&.has-drawer-open .drawer-current-row': {
            isolation: 'isolate',
            zIndex: 1,
            opacity: '1 !important',
          },
          '&.has-drawer-open .drawer-current-row .MuiDataGrid-cell': {
            backgroundColor: 'rgba(87, 125, 173, 0.4)',
          },
          '&.has-drawer-open .MuiDataGrid-columnHeaders, &.has-drawer-open .MuiDataGrid-toolbarContainer, &.has-drawer-open .MuiDataGrid-footerContainer, &.has-drawer-open .MuiDataGrid-toolbar, &.has-drawer-open .MuiDataGrid-cellCheckbox': {
            opacity: 0.1,
            backgroundColor: 'transparent',
            pointerEvents: 'none',
          },
          '&.has-drawer-open .MuiDataGrid-row:hover': {
            opacity: 1,
            backgroundColor: 'rgba(87, 125, 173, 0.2) !important',
          },
          // ============================================================================
          // Mass Update Highlighting - Editable cells when multiple rows selected
          // ============================================================================
          '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell.mass-update-target': {
            backgroundColor: 'rgba(24, 144, 255, 0.12)',
            borderLeft: '2px solid rgba(24, 144, 255, 0.4)',
          },
          '& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell.mass-update-target:hover': {
            backgroundColor: 'rgba(24, 144, 255, 0.2)',
          },
          '& .MuiDataGrid-cell--editing.mass-update-target': {
            backgroundColor: 'rgba(24, 144, 255, 0.2) !important',
            boxShadow: 'inset 0 0 0 2px rgba(24, 144, 255, 0.5)',
          },
          // ============================================================================
          // NCO Expand Button
          // ============================================================================
          '& .nco-expand-button': {
            pointerEvents: 'var(--nco-expand-events) !important',
            color: 'var(--nco-expand-color) !important',
          },
          ...hiddenSearchColumnStyles,
        }),
        pagination: ({ theme }) => ({
          color: theme.palette.text.secondary,
        }),
        footerContainer: ({ theme }) => ({
          backgroundColor: theme.palette.background.defaultdim,
          '& .MuiTablePagination-root': {
            backgroundColor: 'transparent',
          },
          '& .MuiTablePagination-toolbar': {
            backgroundColor: 'transparent',
          },
          '& .MuiToolbar-root': {
            backgroundColor: 'transparent',
          },
        }),
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {},
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgb(30, 39, 53)',
        },
      },
    },
  },
})
